<template>
  <div class="murphy-drawer">
    <div class="murphy-drawer__header">
      <span class="murphy-drawer__logo">
        <murphy-logo variant="logo" />
      </span>
      <div class="murphy-drawer__title-wrap">
        <div class="murphy-drawer__title" :title="displayTitle">{{ displayTitle }}</div>
        <div class="murphy-drawer__subtitle">{{ headerSubtitle }}</div>
      </div>
      <div class="murphy-drawer__actions">
        <v-btn
          icon
          x-small
          class="murphy-drawer__action"
          :disabled="!canCopy"
          v-auto-tooltip="'Copiar al portapapeles'"
          @click="onCopy"
        >
          <v-icon small>mdi-content-copy</v-icon>
        </v-btn>
        <v-btn
          icon
          x-small
          class="murphy-drawer__action"
          :loading="isLoading"
          :disabled="!canRefresh"
          v-auto-tooltip="'Actualizar consulta Murphy'"
          @click="onRefresh"
        >
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
        <v-btn
          icon
          x-small
          class="murphy-drawer__action"
          :disabled="!canPin"
          v-auto-tooltip="isPinnedResult ? 'Quitar pin' : 'Pinear resultado'"
          @click="onTogglePin"
        >
          <v-icon small :color="isPinnedResult ? 'var(--kln-accent)' : ''">
            {{ isPinnedResult ? 'mdi-pin' : 'mdi-pin-outline' }}
          </v-icon>
        </v-btn>
        <v-btn
          icon
          x-small
          class="murphy-drawer__action"
          v-auto-tooltip="'Colapsar panel'"
          @click="$emit('collapse')"
        >
          <v-icon small>mdi-chevron-left</v-icon>
        </v-btn>
      </div>
    </div>

    <div class="murphy-drawer__body">
      <!-- Estado stale (pin recien reseleccionado post-reload) -->
      <div v-if="isStale" class="murphy-drawer__empty">
        <v-icon size="36" color="var(--kln-text-dim)">mdi-refresh-auto</v-icon>
        <div class="murphy-drawer__empty-text">
          Este pin no tiene datos cargados.<br>
          Haz clic en <b>Actualizar</b> para consultar Murphy con
          <code>{{ queryDisplay }}</code>.
        </div>
      </div>

      <!-- Estado loading -->
      <div v-else-if="isLoading" class="murphy-drawer__empty">
        <v-progress-circular indeterminate color="var(--kln-accent)" size="36" />
        <div class="murphy-drawer__empty-text">Consultando Murphy...</div>
      </div>

      <!-- Estado error -->
      <div v-else-if="isError" class="murphy-drawer__empty murphy-drawer__empty--error">
        <v-icon size="36" color="var(--kln-alert-danger)">mdi-alert-circle</v-icon>
        <div class="murphy-drawer__empty-text">
          {{ errorMessage || 'Error al consultar Murphy' }}
        </div>
        <v-btn small outlined color="var(--kln-accent)" @click="onRefresh">
          <v-icon small left>mdi-refresh</v-icon>Reintentar
        </v-btn>
      </div>

      <!-- Vehículo — DRAWERS-VARIANTS-SPEC §6 (4 zonas) -->
      <template v-else-if="hasResult && kind === 'vehiculo'">
        <div class="murphy-result">
          <!-- ZONA 1 — Crítica. Banner de 2 líneas tri-estado + datos clave
               del vehículo y propietario. El banner cambia color según
               `reporteCategory`:
                 ROJO  → CON REPORTE / PRE-DENUNCIA
                 AZUL  → RECUPERADO (hubo robo, ya resuelto)
                 VERDE → SIN REPORTE
               Línea 1: headline grande (estado consolidado) + timestamp.
               Línea 2 (opcional): descripción del subestado en lenguaje
               legible — distingue CONFIRMADO por fiscalía vs. preliminar
               sin necesitar dos chips encimados en una línea. -->
          <div class="drawer-zone drawer-zone--1">
            <div
              class="murphy-report"
              :class="'murphy-report--' + reporteCategory"
            >
              <div class="murphy-report__row">
                <v-icon
                  small
                  :color="reporteIconColor"
                  class="murphy-report__icon"
                >{{ reporteIcon }}</v-icon>
                <span class="murphy-report__label">{{ reporteHeadline }}</span>
                <span
                  v-if="consultedAgoLabel"
                  class="murphy-report__timestamp"
                >{{ consultedAgoLabel }}</span>
              </div>
              <div
                v-if="reporteSubtitle"
                class="murphy-report__sub"
              >{{ reporteSubtitle }}</div>
            </div>
            <drawer-zone-row zone="zone-1" label="Propietario" :value="vehiculoData.propietario || '—'">
              <template #action>
                <drawer-cross-ref-button
                  v-if="vehiculoData.propietario"
                  icon="mdi-card-account-details-outline"
                  tooltip="Buscar al propietario en Murphy"
                  @click="onSearchOwnerInMurphy"
                />
              </template>
            </drawer-zone-row>
            <drawer-zone-row zone="zone-1" label="Placa" :value="vehiculoData.placa || '—'" />
            <drawer-zone-row zone="zone-1" label="Estado emisor" :value="vehiculoData.estado || '—'" />
            <drawer-zone-row zone="zone-1" label="Vehículo" :value="vehiculoData.vehiculo || '—'" />
            <drawer-zone-row zone="zone-1" label="Color" :value="vehiculoData.color || '—'" />
            <!-- "Domicilio del propietario" solo cuando viene de VEHICULOS
                 EDOMEX (única fuente fidedigna). El campo `domicilio` de
                 ALERTA VEHICULAR es la "DIRECCIÓN DEL ROBO" y se muestra
                 abajo, en la sección de Reporte de robo. -->
            <drawer-zone-row
              v-if="vehiculoData.domicilioPropietario"
              zone="zone-1"
              label="Domicilio del propietario"
              :value="vehiculoData.domicilioPropietario"
            />
          </div>

          <!-- ZONA — Reporte de robo. Solo aparece cuando hay datos en
               `roboData` (alguna fuente con info de robo). Visible incluso
               si el reporte ya fue RECUPERADO porque sigue siendo info
               operativa relevante para el seguimiento. -->
          <div v-if="roboData" class="drawer-zone drawer-zone--2">
            <drawer-section-header
              label="Reporte de robo"
              :collapsible="true"
              :expanded="roboExpanded"
              :alert="reporteCategory === 'robo'"
              @toggle="roboExpanded = !roboExpanded"
            />
            <!-- `zone-4-stacked`: campos como dirección de robo y
                 averiguación previa son strings largos (>30 chars) que
                 con la variante row+ellipsis quedaban truncados (ej.
                 "CARR CHACLO MIXQ..." / "NEZ/FTR/ETN/062/..."). Stacked
                 da label arriba y valor debajo con wrap, sacrificando
                 densidad por legibilidad — prioridad operativa. -->
            <div v-show="roboExpanded" class="drawer-zone__content">
              <drawer-zone-row
                v-if="roboData.fechaRobo"
                zone="zone-4-stacked"
                label="Fecha del robo"
                :value="formatRoboDate(roboData.fechaRobo)"
              />
              <drawer-zone-row
                v-if="roboData.fechaReporte && roboData.fechaReporte !== roboData.fechaRobo"
                zone="zone-4-stacked"
                label="Fecha del reporte"
                :value="roboData.fechaReporte"
              />
              <drawer-zone-row
                v-if="roboData.modalidad"
                zone="zone-4-stacked"
                label="Modalidad"
                :value="roboData.modalidad"
              />
              <drawer-zone-row
                v-if="lugarRobo"
                zone="zone-4-stacked"
                label="Lugar del robo"
                :value="lugarRobo"
              />
              <drawer-zone-row
                v-if="roboData.direccionRobo"
                zone="zone-4-stacked"
                label="Dirección del robo"
                :value="roboData.direccionRobo"
              />
              <drawer-zone-row
                v-if="roboData.averiguacion"
                zone="zone-4-stacked"
                label="Averiguación"
                :value="roboData.averiguacion"
              />
              <drawer-zone-row
                v-if="roboData.averiguacionPrevia && roboData.averiguacionPrevia !== roboData.averiguacion"
                zone="zone-4-stacked"
                label="Averiguación previa"
                :value="roboData.averiguacionPrevia"
              />
              <drawer-zone-row
                v-if="roboData.folioDenuncia"
                zone="zone-4-stacked"
                label="Folio de denuncia"
                :value="roboData.folioDenuncia"
              />
            </div>
          </div>

          <!-- ZONA — Otras placas asociadas al mismo VIN. Aparece solo si
               el VIN tiene historial (>1 placa). Permite saltar a cada placa
               vía cross-ref Murphy. -->
          <div v-if="placasAsociadas.length > 0" class="drawer-zone drawer-zone--3">
            <drawer-section-header
              label="Otras placas con el mismo VIN"
              :count="placasAsociadas.length"
            />
            <div class="drawer-zone__content">
              <div
                v-for="(p, pi) in placasAsociadas"
                :key="'plac-asoc-'+pi"
                class="murphy-placa-asociada"
              >
                <span class="murphy-placa-asociada__value">{{ p }}</span>
                <drawer-cross-ref-button
                  icon="mdi-magnify"
                  tooltip="Consultar esta placa en Murphy"
                  @click="onSearchPlacaAsociada(p)"
                />
              </div>
            </div>
          </div>

          <!-- ZONA — Datos administrativos. Ahora va ANTES de las detecciones
               LPR (operativamente: el operador verifica reporte → propietario
               en zona 1 → si necesita VIN/clase/tipo abre este accordion →
               y solo despues mira el historial de paso por arcos). Colapsado
               por default. -->
          <div class="drawer-zone drawer-zone--4">
            <drawer-section-header
              label="Datos administrativos"
              :collapsible="true"
              :expanded="zone4Expanded"
              @toggle="zone4Expanded = !zone4Expanded"
            />
            <!-- Variante `zone-4-stacked`: label arriba uppercase + valor
                 debajo en una sola columna, con wrap. Antes usábamos
                 `zone-4` que pone label+valor en la misma fila con
                 `white-space: nowrap` + ellipsis y campos como
                 "Observaciones del padrón: PLACAS ACTIVAS" se cortaban
                 a "PLACAS ACTIV...". Stacked deja ver el dato completo. -->
            <div v-show="zone4Expanded" class="drawer-zone__content">
              <drawer-zone-row zone="zone-4-stacked" label="Serie (VIN)" :value="vehiculoData.serie || '—'" />
              <drawer-zone-row v-if="vehiculoData.tarjeta" zone="zone-4-stacked" label="Tarjeta de circulación" :value="vehiculoData.tarjeta" />
              <drawer-zone-row v-if="vehiculoData.tipo" zone="zone-4-stacked" label="Tipo" :value="vehiculoData.tipo" />
              <drawer-zone-row v-if="vehiculoData.motor" zone="zone-4-stacked" label="Motor" :value="vehiculoData.motor" />
              <drawer-zone-row v-if="vehiculoData.rfc" zone="zone-4-stacked" label="RFC del propietario" :value="vehiculoData.rfc" />
              <drawer-zone-row v-if="vehiculoData.observaciones" zone="zone-4-stacked" label="Observaciones del padrón" :value="vehiculoData.observaciones" />
            </div>
          </div>

          <!-- ZONA 3 — Contextual: detecciones LPR -->
          <div v-if="lprData && lprData.length > 0" class="drawer-zone drawer-zone--3">
            <drawer-section-header
              label="Detecciones LPR"
              :count="lprData.length"
              :alert="lprDataHasRecent"
            />
            <div class="murphy-lpr-accordion">
              <div
                v-for="(lpr, idx) in lprData"
                :key="idx"
                class="murphy-lpr-item"
                :class="{ 'murphy-lpr-item--open': murphyLprExpandedIdx === idx }"
              >
                <button
                  type="button"
                  class="murphy-lpr-header"
                  @click="onLprRowSelect(idx)"
                >
                  <div class="murphy-lpr-header__line1">
                    <v-icon size="14" class="murphy-lpr-chevron">mdi-chevron-right</v-icon>
                    <span
                      v-if="lprHitDisplayNumberByIdx[idx]"
                      class="murphy-lpr-hit-badge"
                      v-auto-tooltip="'Hit #' + lprHitDisplayNumberByIdx[idx] + ' en el mapa'"
                    >{{ lprHitDisplayNumberByIdx[idx] }}</span>
                    <span class="murphy-lpr-header-date">{{ formatLprDateShort(getLprProp(lpr, 'registro')) }}</span>
                    <span class="murphy-lpr-header-arch">{{ getLprProp(lpr, 'arco') }}</span>
                    <v-btn
                      v-if="canLocateLprArco(getLprProp(lpr, 'arco'))"
                      icon
                      x-small
                      class="murphy-lpr-locate"
                      v-auto-tooltip="'Ver arco en el mapa'"
                      @click.stop="onFlyToLprArco(getLprProp(lpr, 'arco'))"
                    >
                      <v-icon size="14">mdi-map-marker</v-icon>
                    </v-btn>
                  </div>
                  <!-- Motivo del hit ALPR cuando es accionable: contiene
                       texto libre del operativo que detectó la placa, p. ej.
                       "PROBABLE PRIV DE LIBERTAD". Antes quedaba escondido
                       dentro del accordion expandido — ahora se promueve
                       al header para que el operador lo vea sin abrir cada
                       fila. Se omite cuando la fuente devuelve placeholder. -->
                  <div
                    v-if="lprMotivo(lpr)"
                    class="murphy-lpr-header__motivo"
                    :title="lprMotivo(lpr)"
                  >{{ lprMotivo(lpr) }}</div>
                </button>
                <div v-if="murphyLprExpandedIdx === idx" class="murphy-lpr-body">
                  <div
                    v-for="(field, fIdx) in getLprExpandedFields(lpr)"
                    :key="fIdx"
                    class="murphy-lpr-field"
                    :class="{ 'murphy-lpr-field--image': field.isImage }"
                  >
                    <span class="murphy-lpr-field-label">{{ field.label }}</span>
                    <template v-if="field.isImage">
                      <img
                        :src="field.value"
                        class="murphy-lpr-field-image"
                        alt="Captura LPR"
                        @click="openLprZoom(field.value)"
                      />
                    </template>
                    <span v-else class="murphy-lpr-field-value">{{ field.value }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </template>

      <!-- Persona -->
      <template v-else-if="hasResult && kind === 'persona'">
        <!-- Toolbar con paginador + toggle filtros. Siempre visible cuando
             hay un result (aunque solo haya 1 persona o cero tras filtros),
             para que el operador pueda quitar filtros que estén escondiendo
             resultados. -->
        <div class="murphy-homonym-controls">
          <div class="murphy-paginator">
            <v-btn
              icon x-small
              :disabled="personas.length <= 1 || murphyPersonaIdx <= 0"
              @click="murphyPersonaIdx--"
            >
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <span class="murphy-paginator__text">
              <template v-if="filteredMurphyPersonas.length > 0">
                {{ Math.min(murphyPersonaIdx, filteredMurphyPersonas.length - 1) + 1 }} de {{ filteredMurphyPersonas.length }}
                <template v-if="murphyHasActiveFilters && filteredMurphyPersonas.length !== personas.length">
                  (de {{ personas.length }})
                </template>
              </template>
              <template v-else>Sin resultados</template>
            </span>
            <v-btn
              icon x-small
              :disabled="personas.length <= 1 || murphyPersonaIdx >= filteredMurphyPersonas.length - 1"
              @click="murphyPersonaIdx++"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
            <v-btn
              icon x-small
              class="murphy-filter-toggle"
              :class="{ 'murphy-filter-toggle--active': murphyHasActiveFilters }"
              @click="murphyFiltersVisible = !murphyFiltersVisible"
              v-auto-tooltip="'Filtrar resultados'"
            >
              <v-icon small :color="murphyHasActiveFilters ? 'var(--kln-accent)' : ''">mdi-filter-variant</v-icon>
            </v-btn>
          </div>
          <!-- Filtros colapsables -->
          <div v-if="murphyFiltersVisible" class="murphy-filters">
            <v-select
              v-model="murphyFilterEstado"
              :items="murphyEstadosDisponibles"
              :menu-props="{ contentClass: 'kln-dense-menu murphy-estados-menu', maxHeight: 260 }"
              dense
              outlined
              hide-details
              clearable
              multiple
              placeholder="Estado(s)"
              class="murphy-filter-input murphy-filter-input--estados"
              @change="murphyPersonaIdx = 0"
            >
              <!-- Cada opción: columna fija con [N] + nombre del estado.
                   Usa tabular-nums + width fijo para que todos los nombres
                   arranquen alineados. Count=0 se atenúa. -->
              <template #item="{ item, on, attrs }">
                <v-list-item v-bind="attrs" v-on="on" class="murphy-estado-opt">
                  <v-list-item-action class="murphy-estado-opt__check">
                    <v-simple-checkbox :value="attrs.inputValue" />
                  </v-list-item-action>
                  <v-list-item-content class="murphy-estado-opt__content">
                    <span class="murphy-estado-opt__count" :class="{ 'murphy-estado-opt__count--zero': !item.count }">
                      [{{ item.count }}]
                    </span>
                    <span class="murphy-estado-opt__name">{{ item.value }}</span>
                  </v-list-item-content>
                </v-list-item>
              </template>

              <!-- Chip de selección: nombre completo con ellipsis si no cabe.
                   Tooltip con el nombre completo para nombres truncados.
                   Si hay múltiples, primero + "+N" para el resto. -->
              <template #selection="{ item, index }">
                <span
                  v-if="index === 0"
                  class="murphy-estado-chip"
                  :title="item.value"
                >{{ item.value }}</span>
                <span v-else-if="index === 1" class="murphy-estado-chip murphy-estado-chip--more">
                  +{{ murphyFilterEstado.length - 1 }}
                </span>
              </template>
            </v-select>
            <v-text-field
              v-model="murphyFilterFecha"
              dense
              outlined
              hide-details
              clearable
              placeholder="Año"
              class="murphy-filter-input murphy-filter-input--year"
              maxlength="4"
              @input="murphyPersonaIdx = 0"
            />
          </div>
        </div>

        <!-- Placeholder cuando los filtros excluyen a todas las personas.
             Ofrece un CTA para quitar filtros sin que el operador tenga
             que adivinar por qué no se muestra nada. -->
        <div
          v-if="!currentPersona && personas.length > 0 && murphyHasActiveFilters"
          class="murphy-no-results"
        >
          <v-icon small class="mr-2" color="var(--kln-text-dim)">mdi-filter-off-outline</v-icon>
          <span>Los filtros activos excluyen a los {{ personas.length }} resultado{{ personas.length === 1 ? '' : 's' }}.</span>
          <v-btn x-small text color="var(--kln-accent)" class="ml-2" @click="_clearMurphyFilters">
            Quitar filtros
          </v-btn>
        </div>

        <div v-if="currentPersona" class="murphy-result">
          <!-- ZONA 1 — Crítica. Datos generales del homonimo actual. -->
          <div class="drawer-zone drawer-zone--1">
            <!-- Foto visible a primera vista (no la escondemos en Zona 4).
                 Clickable para ampliar. Si no hay foto, se omite. -->
            <div v-if="currentPersona.foto" class="persona-photo-hero">
              <img
                :src="'data:image/jpeg;base64,' + currentPersona.foto"
                class="persona-photo-hero__img"
                alt="Fotografía"
                @click="openPersonaPhoto(currentPersona.foto)"
              />
            </div>
            <drawer-zone-row
              zone="zone-1"
              label="Nombre"
              :value="personaNombre"
            />
            <drawer-zone-row
              zone="zone-1"
              label="Nacimiento"
              :value="currentPersona.fecha_nac || '—'"
            />
            <drawer-zone-row
              v-if="personaEdad != null"
              zone="zone-1"
              label="Edad"
              :value="personaEdad + ' años'"
            />
            <drawer-zone-row
              zone="zone-1"
              label="Estado de origen"
              :value="currentPersona.estado || (currentPersona.ine && currentPersona.ine.estado) || '—'"
            />
            <drawer-zone-row
              v-if="currentPersona.ine && currentPersona.ine.curp"
              zone="zone-1"
              label="CURP"
              :value="currentPersona.ine.curp"
            >
              <template #action>
                <drawer-cross-ref-button
                  icon="mdi-content-copy"
                  tooltip="Copiar CURP"
                  @click="onCopyCurp"
                />
              </template>
            </drawer-zone-row>
          </div>

          <!-- ZONA 2 — Acción: contexto modulado por info sensible.
               Tres estados visuales del mensaje: alerta (rojo, con info
               sensible), OK (verde, sin antecedentes), neutro (mute, sin
               data). Da feedback inmediato al operador sobre si la persona
               "está limpia" sin tener que scrollear las secciones. -->
          <div class="drawer-zone drawer-zone--2">
            <div
              class="drawer-zone__context"
              :class="{
                'drawer-zone__context--alert': personaHasSensitiveInfo,
                'drawer-zone__context--ok': !personaHasSensitiveInfo && personaHasCheckedData,
              }"
            >
              <v-icon
                v-if="personaHasSensitiveInfo"
                x-small
                color="var(--kln-alert-danger)"
                class="mr-1"
              >mdi-alert</v-icon>
              <v-icon
                v-else-if="personaHasCheckedData"
                x-small
                color="var(--kln-status-online)"
                class="mr-1"
              >mdi-check-circle</v-icon>
              {{ personaActionContext }}
            </div>
          </div>

          <!-- ZONA 3 — Contextual (orden modulado por info sensible).
               Con sensible: Mandamientos / Faltas / Penitenciaría primero;
               luego Vehículos / Hijos / Licencias.
               Sin sensible: Vehículos / Hijos / Licencias al frente. -->
          <div class="drawer-zone drawer-zone--3">
            <template v-if="personaHasSensitiveInfo">
              <!-- Mandamientos: alerta. -->
              <template v-if="currentPersona.mandamientos && currentPersona.mandamientos.length">
                <drawer-section-header
                  label="Mandamientos"
                  :count="currentPersona.mandamientos.length"
                  :alert="true"
                />
                <div v-for="(m, mi) in currentPersona.mandamientos" :key="'mand-'+mi" class="murphy-sub-item">
                  <drawer-zone-row zone="zone-3" label="Estado" :value="m.estado || '—'" />
                  <drawer-zone-row zone="zone-3" label="Proceso" :value="m.no_proceso || '—'" />
                  <drawer-zone-row zone="zone-3" label="Delito" :value="m.delito || '—'" />
                  <drawer-zone-row zone="zone-3" label="Fecha" :value="m.fecha_orden || '—'" />
                  <drawer-zone-row zone="zone-3" label="Tipo" :value="m.tipo_mandamiento || '—'" />
                </div>
              </template>
              <!-- Faltas Administrativas: alerta. -->
              <template v-if="currentPersona.faltas && currentPersona.faltas.length">
                <drawer-section-header
                  label="Faltas Administrativas"
                  :count="currentPersona.faltas.length"
                  :alert="true"
                />
                <div v-for="(f, fi) in currentPersona.faltas" :key="'falta-'+fi" class="murphy-sub-item">
                  <drawer-zone-row zone="zone-3" label="Fecha" :value="f.fecha_hora_detencion || '—'" />
                  <drawer-zone-row zone="zone-3" label="Municipio" :value="f.municipio || '—'" />
                  <drawer-zone-row zone="zone-3" label="Motivo" :value="f.motivo_catalogo || '—'" />
                </div>
              </template>
              <!-- Info Penitenciaria: alerta. -->
              <template v-if="currentPersona.penitenciaria && currentPersona.penitenciaria.length">
                <drawer-section-header
                  label="Info. Penitenciaria"
                  :count="currentPersona.penitenciaria.length"
                  :alert="true"
                />
                <div v-for="(p, pi) in currentPersona.penitenciaria" :key="'penit-'+pi" class="murphy-sub-item">
                  <drawer-zone-row zone="zone-3" label="Centro" :value="p.centro || '—'" />
                  <drawer-zone-row zone="zone-3" label="Delito" :value="p.delito || '—'" />
                </div>
              </template>
            </template>

            <!-- Vehículos (con cross-ref placa→Murphy en cada uno). -->
            <template v-if="currentPersona.vehiculos && currentPersona.vehiculos.length">
              <drawer-section-header
                label="Vehículos"
                :count="currentPersona.vehiculos.length"
              />
              <div v-for="(v, vi) in currentPersona.vehiculos" :key="'veh-'+vi" class="murphy-sub-item">
                <drawer-zone-row zone="zone-3" label="Placa" :value="v.placa || '—'">
                  <template #action>
                    <drawer-cross-ref-button
                      v-if="v.placa && v.placa !== '—'"
                      icon="mdi-car"
                      tooltip="Buscar placa en Murphy"
                      @click="onSearchPlacaFromPerson(v.placa)"
                    />
                  </template>
                </drawer-zone-row>
                <drawer-zone-row zone="zone-3" label="Vehículo" :value="v.vehiculo || '—'" />
                <drawer-zone-row zone="zone-3" label="Color" :value="v.color || '—'" />
              </div>
            </template>

            <!-- Hijos. -->
            <template v-if="currentPersona.hijosSinaloa && currentPersona.hijosSinaloa.length">
              <drawer-section-header
                label="Hijos"
                :count="currentPersona.hijosSinaloa.length"
              />
              <div v-for="(h, hi) in currentPersona.hijosSinaloa" :key="'hijo-'+hi" class="murphy-sub-item">
                <drawer-zone-row
                  zone="zone-3"
                  label="Hijo"
                  :value="((h.hijo_nombre || '') + ' ' + (h.hijo_apellido_1 || '') + ' ' + (h.hijo_apellido_2 || '')).trim() || '—'"
                />
                <drawer-zone-row zone="zone-3" label="Nacimiento" :value="h.hijo_fecha_nac || '—'" />
                <drawer-zone-row zone="zone-3" label="CURP" :value="h.hijo_curp || '—'" />
              </div>
            </template>

            <!-- Licencias. -->
            <template v-if="currentPersona.lics && currentPersona.lics.length">
              <drawer-section-header
                label="Licencias"
                :count="currentPersona.lics.length"
              />
              <div v-for="(lic, li) in currentPersona.lics" :key="'lic-'+li" class="murphy-sub-item">
                <drawer-zone-row zone="zone-3" label="Estado" :value="lic.estado || '—'" />
                <drawer-zone-row zone="zone-3" label="Vencimiento" :value="lic.vencimiento || lic.fecha_vigencia || '—'" />
                <drawer-zone-row zone="zone-3" label="Domicilio" :value="lic.domicilio || '—'" />
              </div>
            </template>
          </div>

          <!-- ZONA 4 — Administrativa (colapsable): INE, nacimiento,
               matrimonios, IMSS, foto. -->
          <div class="drawer-zone drawer-zone--4">
            <drawer-section-header
              label="Datos administrativos"
              :collapsible="true"
              :expanded="zone4Expanded"
              @toggle="zone4Expanded = !zone4Expanded"
            />
            <div v-show="zone4Expanded" class="drawer-zone__content">
              <!-- INE -->
              <template v-if="currentPersona.ine">
                <drawer-section-header label="INE" />
                <drawer-zone-row zone="zone-4" label="Clave Elector" :value="currentPersona.ine.clave || '—'" />
                <drawer-zone-row zone="zone-4" label="Municipio" :value="currentPersona.ine.municipio || '—'" />
                <drawer-zone-row zone="zone-4" label="Domicilio" :value="currentPersona.ine.domicilio || '—'" />
              </template>

              <!-- Acta de Nacimiento -->
              <template v-if="currentPersona.nacimientosSinaloa && currentPersona.nacimientosSinaloa.length">
                <drawer-section-header label="Acta de Nacimiento" />
                <div v-for="(nac, ni) in currentPersona.nacimientosSinaloa" :key="'nac-'+ni" class="murphy-sub-item">
                  <drawer-zone-row zone="zone-4" label="Estado" :value="nac.estado || '—'" />
                  <drawer-zone-row zone="zone-4" label="Municipio" :value="nac.municipio || '—'" />
                  <drawer-zone-row zone="zone-4" label="Padre" :value="nac.padre || '—'" />
                  <drawer-zone-row zone="zone-4" label="Madre" :value="nac.madre || '—'" />
                </div>
              </template>

              <!-- Matrimonios -->
              <template v-if="currentPersona.matrimoniosSinaloa && currentPersona.matrimoniosSinaloa.length">
                <drawer-section-header
                  label="Matrimonios"
                  :count="currentPersona.matrimoniosSinaloa.length"
                />
                <div v-for="(mat, mati) in currentPersona.matrimoniosSinaloa" :key="'mat-'+mati" class="murphy-sub-item">
                  <drawer-zone-row zone="zone-4" label="Pareja" :value="mat.pareja || '—'" />
                  <drawer-zone-row zone="zone-4" label="Régimen" :value="mat.regimen || '—'" />
                  <drawer-zone-row zone="zone-4" label="Registro" :value="mat.registro || '—'" />
                </div>
              </template>

              <!-- IMSS -->
              <template v-if="currentPersona.imss && currentPersona.imss.length">
                <drawer-section-header
                  label="IMSS"
                  :count="currentPersona.imss.length"
                />
                <div v-for="(imss, ii) in currentPersona.imss" :key="'imss-'+ii" class="murphy-sub-item">
                  <drawer-zone-row zone="zone-4" label="Patrón" :value="imss.nom_patron || imss.nombre_patron || '—'" />
                  <drawer-zone-row
                    zone="zone-4"
                    label="Dirección"
                    :value="(imss.domicilio_patron || '') + (imss.localidad_patron ? ', ' + imss.localidad_patron : '') || '—'"
                  />
                </div>
              </template>

            </div>
          </div>
        </div>
      </template>

      <!-- Sin resultado y sin pin seleccionado -->
      <div v-else class="murphy-drawer__empty">
        <v-icon size="36" color="var(--kln-text-dim)">mdi-card-account-details-outline</v-icon>
        <div class="murphy-drawer__empty-text">
          Usa el buscador Murphy arriba para consultar placas o personas.
        </div>
      </div>
    </div>

    <!-- LPR image lightbox -->
    <div
      v-show="murphyLprZoomSrc"
      class="lpr-zoom-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Captura LPR ampliada"
      @click.self="closeLprZoom"
    >
      <button
        type="button"
        class="lpr-zoom-close"
        @click.stop="closeLprZoom"
        v-auto-tooltip="'Cerrar (Esc)'"
      >
        <v-icon size="20">mdi-close</v-icon>
      </button>
      <div class="lpr-zoom-toolbar" @click.stop>
        <button
          type="button"
          class="lpr-zoom-btn"
          @click="zoomOutLpr"
          :disabled="murphyLprZoomScale <= 1"
          v-auto-tooltip="'Alejar (-)'"
        >
          <v-icon size="18">mdi-magnify-minus-outline</v-icon>
        </button>
        <span class="lpr-zoom-level">{{ Math.round(murphyLprZoomScale * 100) }}%</span>
        <button
          type="button"
          class="lpr-zoom-btn"
          @click="zoomInLpr"
          :disabled="murphyLprZoomScale >= 4"
          v-auto-tooltip="'Acercar (+)'"
        >
          <v-icon size="18">mdi-magnify-plus-outline</v-icon>
        </button>
        <button
          type="button"
          class="lpr-zoom-btn"
          @click="zoomResetLpr"
          :disabled="murphyLprZoomScale === 1"
          v-auto-tooltip="'Ajustar (0)'"
        >
          <v-icon size="18">mdi-fit-to-page-outline</v-icon>
        </button>
      </div>
      <div class="lpr-zoom-stage" @click.self="closeLprZoom">
        <img
          :src="murphyLprZoomSrc"
          class="lpr-zoom-image"
          :style="{ transform: 'scale(' + murphyLprZoomScale + ')' }"
          alt="Captura LPR ampliada"
          @click.stop="toggleLprZoomScale"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import MurphyLogo from '@/components/map-v2/utils/MurphyLogo.vue'
import { formatTimeAgo, splitMexicanFullName } from '@/components/map-v2/data/murphy-helpers'
import { categorizeReporte } from '@/components/map-v2/data/murphy-vehiculo-extractor'
import {
  DrawerSectionHeader,
  DrawerZoneRow,
  DrawerCrossRefButton,
  DrawerActionButton,
} from './drawer-ui'

// Default de estados pre-seleccionados para el filtro Murphy. Se lee de
// VITE_MURPHY_DEFAULT_ESTADOS como CSV (ej: "MEXICO,CIUDAD DE MEXICO,JALISCO").
// Se aplica al primer abrir el panel de un result que no tenga filterEstado
// persistido en su pin. Si el operador quita los filtros, queda limpio y el
// pin guarda array vacío (no re-aplica el default al rehidratar).
const DEFAULT_MURPHY_ESTADOS = (function() {
  var raw = import.meta.env.VITE_MURPHY_DEFAULT_ESTADOS || ''
  return raw.split(',').map(function(s) { return s.trim().toUpperCase() }).filter(Boolean)
})()

// Normaliza el nombre del estado proveniente del backend para colapsarlo
// al canónico: mayúsculas, sin acentos, sin sufijos como " (ESTADO)" o
// " DE OCCIDENTE", trim. Evita que "MEXICO" y "MEXICO (ESTADO)" aparezcan
// como dos entradas distintas en el dropdown del filtro.
function normalizeEstado(s) {
  if (!s) return ''
  return String(s)
    .toUpperCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')   // quita acentos
    .replace(/\s*\(.*?\)\s*/g, ' ')                      // quita "(...)"
    .replace(/\s+/g, ' ')
    .trim()
}

// Lista fija de los 32 estados + CDMX para el filtro de Murphy. Así el
// operador siempre tiene todas las opciones disponibles (no solo las de
// los resultados del query actual). El matching contra el payload es por
// string exacto en mayúsculas y sin acentos — formato que usa el backend.
const MURPHY_ESTADOS_MX = [
  'AGUASCALIENTES',
  'BAJA CALIFORNIA',
  'BAJA CALIFORNIA SUR',
  'CAMPECHE',
  'CHIAPAS',
  'CHIHUAHUA',
  'CIUDAD DE MEXICO',
  'COAHUILA',
  'COLIMA',
  'DURANGO',
  'GUANAJUATO',
  'GUERRERO',
  'HIDALGO',
  'JALISCO',
  'MEXICO',
  'MICHOACAN',
  'MORELOS',
  'NAYARIT',
  'NUEVO LEON',
  'OAXACA',
  'PUEBLA',
  'QUERETARO',
  'QUINTANA ROO',
  'SAN LUIS POTOSI',
  'SINALOA',
  'SONORA',
  'TABASCO',
  'TAMAULIPAS',
  'TLAXCALA',
  'VERACRUZ',
  'YUCATAN',
  'ZACATECAS',
]

// Labels legibles para los campos crudos que devuelve Murphy en cada
// detección LPR.
const LPR_FIELD_LABELS = {
  registro: 'Fecha / Hora',
  arco: 'Arco',
  sentido: 'Sentido',
  direccion: 'Dirección',
  motivo: 'Motivo',
  reporte: 'Reporte',
  carril: 'Carril',
  velocidad: 'Velocidad',
  confianza: 'Confianza',
  color: 'Color',
  marca: 'Marca',
  modelo: 'Modelo',
  imagen: 'Imagen',
}

// Campos que contienen blobs de imagen.
const LPR_IMAGE_FIELDS = {
  imagen: true,
  foto: true,
  image: true,
  photo: true,
}

// Functional DetailRow component — mirrors MapDrawer implementation exactly.
const DetailRow = {
  name: 'DetailRow',
  functional: true,
  props: {
    label: String,
    value: [String, Number, Function],
    icon: { type: String, default: 'mdi-chevron-right' },
    html: { type: Boolean, default: false },
  },
  render(h, ctx) {
    const {
      label, value, icon, html,
    } = ctx.props
    let display
    if (typeof value === 'function') {
      display = '—'
    } else {
      display = value != null ? String(value) : '—'
    }
    const valueNode = html
      ? h('span', { class: 'detail-row__value', domProps: { innerHTML: display } })
      : h('span', { class: 'detail-row__value' }, [display])
    return h('div', { class: 'detail-row' }, [
      h('v-icon', { props: { xSmall: true }, class: 'detail-row__icon' }, [icon]),
      h('span', { class: 'detail-row__label' }, [label]),
      valueNode,
    ])
  },
}

export default {
  name: 'MurphyDrawer',

  components: {
    'detail-row': DetailRow,
    MurphyLogo,
    DrawerSectionHeader,
    DrawerZoneRow,
    DrawerCrossRefButton,
    DrawerActionButton,
  },

  props: {
    // Indice {ARCO_NAME_UPPER: monitorId} de los LPRs del mapa con
    // coordenadas validas. Viene de MapV2.lprArcoIndex. Si el arco de una
    // deteccion matchea una key, el boton "Ver en mapa" se muestra; si no,
    // se omite porque no podemos llevar al operador a ningun lado.
    lprArcoIndex: {
      type: Object,
      default: function() { return {} },
    },
  },

  data() {
    return {
      murphyLprExpandedIdx: -1,
      murphyLprZoomSrc: '',
      murphyLprZoomScale: 1,
      murphyPersonaIdx: 0,
      murphyFiltersVisible: false,
      murphyFilterEstado: [],
      murphyFilterFecha: '',
      zone4Expanded: false,
      // Sección "Reporte de robo" — abierta por default si hay reporte
      // activo (ROJO/AZUL); colapsada en SIN REPORTE. La inicialización real
      // ocurre en el watcher de murphyResult.id.
      roboExpanded: false,
    }
  },

  watch: {
    // Cuando cambia el id del result activo, hidratamos el view state
    // local desde los campos persistidos del result (que pueden venir de
    // un pin con filtros y paginador previamente guardados). Usamos un
    // flag para suprimir los watchers individuales durante la
    // inicializacion y evitar re-dispachar updateMurphyViewState con los
    // mismos valores.
    // Auto-emit de los hits LPR al mapa: cuando cambia el result
    // Murphy activo (o el set de detecciones), el mapa se actualiza
    // sin que el operador tenga que tocar nada. Pasa null si no hay
    // hits para limpiar layers anteriores.
    lprHitMarkers: {
      immediate: true,
      handler: function onLprHitMarkersChange(hits) {
        this.$emit('show-lpr-route', (hits && hits.length) ? hits : null)
      },
    },

    // Auto-expandir "Reporte de robo" cuando aparece roboData con reporte
    // activo. Watcher dedicado (separado del id-watcher) porque el result
    // de Murphy puede llegar con `data.reporte` y `roboData` en momentos
    // distintos: el id se setea ANTES de que el extractor termine, así
    // que el watcher de id no captura la primera carga. Al observar
    // `murphyResult` con deep, este se dispara en cada update real.
    murphyResult: {
      immediate: true,
      deep: false,
      handler: function onMurphyResultChange(r, prev) {
        // Cambia solo cuando lo que importa (roboData o categoría) cambió,
        // para no pisar el toggle manual del operador en updates
        // accidentales (filtros, paginador, etc).
        var prevHasRobo = !!(prev && prev.roboData)
        var currHasRobo = !!(r && r.roboData)
        var prevCat = prev ? this.reporteCategoryFor(prev) : ''
        var currCat = r ? this.reporteCategoryFor(r) : ''
        if (prevHasRobo === currHasRobo && prevCat === currCat) return
        this.roboExpanded = !!(r && r.roboData && currCat === 'robo')
      },
    },

    'murphyResult.id': {
      immediate: true,
      handler: function onMurphyResultIdChange() {
        const r = this.murphyResult
        this._syncingFromStore = true
        if (!r) {
          this.murphyPersonaIdx = 0
          // Default del env solo para results sin filtros previos.
          this.murphyFilterEstado = DEFAULT_MURPHY_ESTADOS.slice()
          this.murphyFilterFecha = ''
          this.murphyFiltersVisible = false
        } else {
          this.murphyPersonaIdx = r.personaIdx || 0
          // Hidratación del filtro estado con backwards-compat:
          //   - undefined (pin previo al multi-select)  → aplica default env.
          //   - string                                  → wrap en array [string].
          //   - array                                   → usa tal cual.
          if (r.filterEstado === undefined || r.filterEstado === null) {
            this.murphyFilterEstado = DEFAULT_MURPHY_ESTADOS.slice()
          } else if (Array.isArray(r.filterEstado)) {
            this.murphyFilterEstado = r.filterEstado.slice()
          } else {
            this.murphyFilterEstado = [r.filterEstado]
          }
          this.murphyFilterFecha = r.filterFecha || ''
          this.murphyFiltersVisible = !!r.filtersVisible
        }
        // Si aplicamos el default del env en un result fresco (sin preferencia
        // explícita de filtersVisible), abrimos los filtros para que el
        // operador vea que hay un filtro activo y pueda ajustarlo.
        if (r && r.filtersVisible === undefined && this.murphyFilterEstado.length) {
          this.murphyFiltersVisible = true
        }
        this.$nextTick(() => {
          this._syncingFromStore = false
          // Auto-clear del filtro de estado si excluye a TODAS las personas
          // devueltas. Caso típico: el default env apunta a EdoMex/CDMX/JAL
          // y el operador buscó a alguien de otro estado sin saber que el
          // filtro estaba activo. Solo dispara al cargar/cambiar result;
          // no interfiere con cambios manuales del operador.
          if (this.kind === 'persona'
              && this.personas.length > 0
              && this.filteredMurphyPersonas.length === 0
              && this.murphyFilterEstado.length > 0) {
            this.murphyFilterEstado = []
          }
        })
      },
    },
    murphyPersonaIdx(v) {
      if (this._syncingFromStore) return
      this.updateMurphyViewState({ personaIdx: v })
    },
    murphyFilterEstado(v) {
      if (this._syncingFromStore) return
      // Persistimos siempre como array (vacío o con valores). Mantener null
      // para compat con pins viejos solo si la variable está vacía.
      this.updateMurphyViewState({ filterEstado: Array.isArray(v) ? v : [] })
    },
    murphyFilterFecha(v) {
      if (this._syncingFromStore) return
      this.updateMurphyViewState({ filterFecha: v || '' })
    },
    murphyFiltersVisible(v) {
      if (this._syncingFromStore) return
      this.updateMurphyViewState({ filtersVisible: !!v })
    },
  },

  created() {
    this._syncingFromStore = false
  },

  computed: {
    ...mapState('map-v2', ['murphyResult']),
    ...mapGetters('map-v2', ['isMurphyPinned', 'hasMurphyResult']),

    kind() {
      return this.murphyResult ? this.murphyResult.kind : null
    },

    title() {
      return this.murphyResult ? this.murphyResult.title : 'Murphy'
    },

    // Titulo mostrado en el header del panel. Para personas con homonimos,
    // refleja el nombre del homonimo actualmente seleccionado (currentPersona)
    // en lugar del primer resultado. El title del pin (tab) sigue siendo
    // estable — solo cambia lo que el operador ve en la cabecera.
    displayTitle() {
      if (!this.murphyResult) return 'Murphy'
      if (this.kind === 'persona' && this.currentPersona) {
        return this.personaNombre || this.murphyResult.title
      }
      return this.murphyResult.title
    },

    status() {
      return this.murphyResult ? this.murphyResult.status : null
    },

    errorMessage() {
      return this.murphyResult ? this.murphyResult.errorMessage : null
    },

    isStale() { return this.status === 'stale' },
    isLoading() { return this.status === 'loading' },
    isError() { return this.status === 'error' },
    isFresh() { return this.status === 'fresh' },

    hasResult() {
      if (!this.murphyResult) return false
      if (this.kind === 'vehiculo') return !!this.murphyResult.data
      if (this.kind === 'persona') return (this.murphyResult.personas || []).length > 0
      return false
    },

    isPinnedResult() {
      if (!this.murphyResult) return false
      return this.isMurphyPinned(this.murphyResult.id)
    },

    canCopy() { return this.isFresh && this.hasResult },
    canRefresh() { return !!this.murphyResult && !this.isLoading },
    canPin() { return !!this.murphyResult },

    headerSubtitle() {
      if (!this.murphyResult) return ''
      if (this.isStale) return '— pineado'
      if (this.isLoading) return 'consultando...'
      if (this.isError) return 'error'
      return formatTimeAgo(this.murphyResult.fetchedAt)
    },

    queryDisplay() {
      if (!this.murphyResult) return ''
      const qp = this.murphyResult.queryParams || {}
      if (this.kind === 'vehiculo') return qp.placa || ''
      const parts = [qp.nombre, qp.apellido_1, qp.apellido_2].filter(Boolean)
      return parts.join(' ') || this.title
    },

    vehiculoData() {
      return (this.murphyResult && this.murphyResult.data) || {}
    },

    roboData() {
      return (this.murphyResult && this.murphyResult.roboData) || null
    },

    placasAsociadas() {
      return (this.murphyResult && this.murphyResult.placasAsociadas) || []
    },

    lprData() {
      return (this.murphyResult && this.murphyResult.lprData) || []
    },

    // Categoría visual del banner. 'robo'=rojo, 'recuperado'=azul,
    // 'ok'=verde. Se delega al extractor para mantener la lógica en un
    // solo lugar (mismo enum lo lee MurphyPanel y este drawer).
    reporteCategory() {
      return this.reporteCategoryFor(this.murphyResult)
    },

    isRoboReport() {
      return this.reporteCategory === 'robo'
    },

    reporteIcon() {
      if (this.reporteCategory === 'robo') return 'mdi-alert'
      if (this.reporteCategory === 'recuperado') return 'mdi-check-decagram'
      return 'mdi-check-circle'
    },

    reporteIconColor() {
      if (this.reporteCategory === 'robo') return 'var(--kln-alert-danger)'
      if (this.reporteCategory === 'recuperado') return 'var(--kln-accent)'
      return 'var(--kln-alert-success)'
    },

    // Línea 1 del banner: estado consolidado del reporte. Mapea cualquier
    // combinación de ESTATUS.reporte + ALERTA.estatus a UNA etiqueta clara.
    // Una pre-denuncia "confirmada por fiscalía" es CON REPORTE pleno; una
    // pre-denuncia sin confirmación pasa a "PRE-DENUNCIA". Esto evita el
    // par contradictorio "PREDENUNCIA CONFIRMADO" en el banner.
    reporteHeadline() {
      var cat = this.reporteCategory
      if (cat === 'recuperado') return 'RECUPERADO'
      if (cat === 'ok') return 'SIN REPORTE'
      // cat === 'robo'
      var rd = this.roboData
      var alertaConfirmada = rd && rd.estatusOficial === 'CONFIRMADO'
      var soloPredenuncia = rd && rd.reporteEstatus === 'PREDENUNCIA' && !alertaConfirmada
      if (soloPredenuncia) return 'PRE-DENUNCIA'
      return 'CON REPORTE'
    },

    // Línea 2 del banner: descripción legible del subestado para que el
    // operador entienda QUÉ tipo de reporte es sin revelar lenguaje técnico.
    // Vacía cuando no agrega info (SIN REPORTE) o no hay roboData.
    reporteSubtitle() {
      var cat = this.reporteCategory
      if (cat === 'ok') return ''
      var rd = this.roboData
      if (!rd) return ''
      if (cat === 'recuperado') return 'Vehículo recuperado'
      // cat === 'robo'
      if (rd.estatusOficial === 'CONFIRMADO') return 'Confirmado por fiscalía'
      if (rd.reporteEstatus === 'PREDENUNCIA') return 'Reporte preliminar — sin confirmación de fiscalía'
      return ''
    },

    // Combina entidad + municipio del robo en un string legible. Se omite
    // si solo hay uno de los dos (en cuyo caso usar el campo crudo).
    lugarRobo() {
      if (!this.roboData) return ''
      var ent = this.roboData.entidadRobo
      var mun = this.roboData.municipioRobo
      if (ent && mun) return mun + ', ' + ent
      return mun || ent || ''
    },

    // Contexto de acción del vehículo — solo aparece con reporte activo.
    vehiculoActionContext() {
      if (this.isRoboReport) {
        return 'Reporte activo: ' + (this.vehiculoData.reporte || 'ROBADO') + '.'
      }
      return ''
    },

    // Timestamp corto ("hace 17m") para mostrar inline en el banner
    // SIN REPORTE — reemplaza el texto repetitivo "Sin reportes activos.
    // Consultado hace 17m." que estaba en zona 2.
    consultedAgoLabel() {
      if (!this.murphyResult || !this.murphyResult.fetchedAt) return ''
      return formatTimeAgo(this.murphyResult.fetchedAt)
    },

    // LPR data con detecciones recientes (últimas 6h) — dispara tratamiento
    // de alerta en el section header.
    lprDataHasRecent() {
      if (!this.lprData || !this.lprData.length) return false
      var sixHoursAgo = Date.now() - 6 * 60 * 60 * 1000
      for (var i = 0; i < this.lprData.length; i++) {
        var ts = this.getLprProp(this.lprData[i], 'registro')
        if (!ts) continue
        var n = this.parseLprRegistro(ts)
        if (!isNaN(n) && n >= sixHoursAgo) return true
      }
      return false
    },

    // Hits LPR enriquecidos con timestamp, arco y número de orden para
    // dibujar puntos numerados sobre el mapa (paridad con legacy
    // RouteLprStore.createRoute). Cada hit se renderea como un círculo
    // separado con offset aleatorio relativo al arco para que múltiples
    // hits en el mismo arco no se solapen.
    //   - lprIndex: índice 0-based en lprData original (sin orden)
    //   - displayNumber: número visible en el marker. Convención legacy:
    //       el más reciente = N (mayor), el más viejo = 1.
    //   - ts: timestamp ms para mantener orden cronológico
    //   - arco: nombre normalizado (upper/trim) del arco LPR
    lprHitMarkers() {
      if (!this.lprData || !this.lprData.length) return []
      var rows = []
      for (var i = 0; i < this.lprData.length; i++) {
        var arco = this.getLprProp(this.lprData[i], 'arco')
        if (!arco || arco === '—') continue
        var key = String(arco).trim().toUpperCase()
        if (!this.lprArcoIndex[key]) continue
        var ts = this.getLprProp(this.lprData[i], 'registro')
        var n = this.parseLprRegistro(ts)
        if (isNaN(n)) continue
        rows.push({ lprIndex: i, arco: key, ts: n })
      }
      // Numeración legacy: el hit más reciente lleva el número MAYOR
      // (length), el más viejo lleva 1. Sort asc y asignar reverso.
      rows.sort(function(a, b) { return a.ts - b.ts })
      var total = rows.length
      for (var j = 0; j < rows.length; j++) {
        rows[j].displayNumber = total - j
      }
      return rows
    },

    // Mapa lprIndex → displayNumber para que el template pueda mostrar
    // el badge numérico en cada fila de la tabla, alineado con el
    // número del punto correspondiente en el mapa.
    lprHitDisplayNumberByIdx() {
      var out = {}
      var hits = this.lprHitMarkers
      for (var i = 0; i < hits.length; i++) {
        out[hits[i].lprIndex] = hits[i].displayNumber
      }
      return out
    },

    // Edad derivada de la fecha de nacimiento (solo dígitos ISO yyyy-mm-dd
    // o yyyy/mm/dd — Murphy devuelve en esos formatos). Si el formato no
    // encaja devuelve null y se oculta el campo.
    personaEdad() {
      var p = this.currentPersona
      if (!p || !p.fecha_nac) return null
      var raw = String(p.fecha_nac).replace(/\//g, '-')
      var m = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)
      if (!m) return null
      var birth = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      if (isNaN(birth.getTime())) return null
      var now = new Date()
      var age = now.getFullYear() - birth.getFullYear()
      var mDiff = now.getMonth() - birth.getMonth()
      if (mDiff < 0 || (mDiff === 0 && now.getDate() < birth.getDate())) age--
      return age >= 0 && age < 150 ? age : null
    },

    personaHasSensitiveInfo() {
      var p = this.currentPersona
      if (!p) return false
      return !!((p.mandamientos && p.mandamientos.length)
        || (p.faltas && p.faltas.length)
        || (p.penitenciaria && p.penitenciaria.length))
    },

    // True cuando la persona tiene suficiente data consultada como para
    // que un "sin antecedentes" sea afirmación confiable. Exige que INE
    // o IMSS o algún registro base haya devuelto datos — sin eso, la
    // ausencia de antecedentes es más "no encontramos nada que decir"
    // que "esta persona está limpia".
    personaHasCheckedData() {
      var p = this.currentPersona
      if (!p) return false
      if (p.ine) return true
      if (p.imss && p.imss.length) return true
      if (p.nacimientosSinaloa && p.nacimientosSinaloa.length) return true
      if (p.lics && p.lics.length) return true
      if (p.vehiculos && p.vehiculos.length) return true
      return false
    },

    personaActionContext() {
      var p = this.currentPersona
      if (!p) return ''
      if (this.personaHasSensitiveInfo) {
        var parts = []
        if (p.mandamientos && p.mandamientos.length) parts.push(p.mandamientos.length + ' mandamientos')
        if (p.faltas && p.faltas.length) parts.push(p.faltas.length + ' faltas administrativas')
        if (p.penitenciaria && p.penitenciaria.length) parts.push(p.penitenciaria.length + ' registros penitenciarios')
        return parts.join(' · ') + '.'
      }
      return 'Sin antecedentes registrados en las fuentes consultadas.'
    },

    // ── Persona computeds ────────────────────────────────────────
    personas() {
      return (this.murphyResult && this.murphyResult.personas) || []
    },

    // Items del v-select. Estructura: { text, value, count } donde text y
    // value son el nombre del estado limpio (el `[N]` se renderiza aparte
    // en el slot #item usando una columna fija). value queda en MAYÚSCULAS
    // para matching exacto del filtro.
    murphyEstadosDisponibles() {
      // Contar personas por estado del result actual (unfiltered), usando
      // la forma canónica — así "MEXICO" y "MEXICO (ESTADO)" suman al
      // mismo cubo en vez de aparecer como dos opciones distintas.
      const counts = {}
      const list = this.personas || []
      for (let i = 0; i < list.length; i++) {
        const p = list[i]
        const raw = (p && p.estado) || (p && p.ine && p.ine.estado) || ''
        const key = normalizeEstado(raw)
        if (key) counts[key] = (counts[key] || 0) + 1
      }

      // Union de: 32 estados fijos + cualquier estado custom que haya en
      // los results (ya normalizado) + estados seleccionados (chips).
      const allKeys = {}
      for (let k = 0; k < MURPHY_ESTADOS_MX.length; k++) allKeys[MURPHY_ESTADOS_MX[k]] = true
      Object.keys(counts).forEach(function(k) { allKeys[k] = true })
      if (Array.isArray(this.murphyFilterEstado)) {
        for (let s = 0; s < this.murphyFilterEstado.length; s++) {
          const e = this.murphyFilterEstado[s]
          if (e) allKeys[e] = true
        }
      }

      return Object.keys(allKeys).sort().map(function(name) {
        return { text: name, value: name, count: counts[name] || 0 }
      })
    },

    filteredMurphyPersonas() {
      const list = this.personas
      if (!list.length) return list

      const estados = Array.isArray(this.murphyFilterEstado) ? this.murphyFilterEstado : []
      const fecha = (this.murphyFilterFecha || '').trim()

      if (!estados.length && !fecha) return list

      return list.filter((p) => {
        // Multi-select: normalizamos el estado del persona (quita acentos,
        // sufijos como "(ESTADO)") y matcheamos contra cualquiera de los
        // seleccionados (que también están en forma canónica).
        if (estados.length) {
          const raw = (p && p.estado) || (p && p.ine && p.ine.estado) || ''
          const canon = normalizeEstado(raw)
          if (!canon || estados.indexOf(canon) === -1) return false
        }
        // Filtro progresivo de fecha: si el usuario escribe "1990" filtra
        // por año; "1990-03" por año+mes; "1990-03-15" por fecha exacta.
        if (fecha && p.fecha_nac) {
          if (p.fecha_nac.indexOf(fecha) !== 0) return false
        } else if (fecha && !p.fecha_nac) {
          return false
        }
        return true
      })
    },

    murphyHasActiveFilters() {
      var estadoActive = Array.isArray(this.murphyFilterEstado) && this.murphyFilterEstado.length > 0
      var fechaActive = this.murphyFilterFecha && this.murphyFilterFecha.trim()
      return !!(estadoActive || fechaActive)
    },

    currentPersona() {
      const list = this.filteredMurphyPersonas
      if (!list.length) return null
      const idx = Math.min(this.murphyPersonaIdx, list.length - 1)
      return list[idx]
    },

    personaNombre() {
      const p = this.currentPersona
      if (!p) return '—'
      return [(p.nombre || ''), (p.apellido_1 || ''), (p.apellido_2 || '')].join(' ').trim() || '—'
    },
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this._onLprZoomKeydown)
    // Limpiar los puntos LPR del mapa al cerrar el drawer Murphy. Sin
    // esto, los hits del result anterior quedan dibujados aunque el
    // panel no esté visible.
    this.$emit('show-lpr-route', null)
  },

  methods: {
    ...mapActions('map-v2', ['refetchMurphyResult', 'pinCurrentMurphy', 'unpinMurphy', 'updateMurphyViewState']),

    _clearMurphyFilters() {
      this.murphyFilterEstado = []
      this.murphyFilterFecha = ''
      this.murphyPersonaIdx = 0
    },

    // Cross-ref vehículo→persona: busca al propietario en Murphy.
    // El backend devuelve el propietario como string completo "NOMBRE APELLIDO1
    // APELLIDO2" sin separar. Aplicamos heurística mexicana (últimos 2 tokens
    // son apellidos paterno y materno, con soporte para prefijos compuestos
    // tipo "DE LA"). El operador puede ajustar en el MurphyPanel si la
    // descomposición no es la esperada para casos edge.
    onSearchOwnerInMurphy() {
      var owner = this.vehiculoData && this.vehiculoData.propietario
      if (!owner || owner === '—') return
      var payload = splitMexicanFullName(owner)
      if (!payload.nombre && !payload.ap1) return
      this.$emit('search-murphy-persona', payload)
    },

    // Cross-ref persona→placa: busca una placa del array de vehículos en
    // Murphy. Útil cuando se consulta a una persona y se quiere auditar sus
    // autos.
    onSearchPlacaFromPerson(placa) {
      if (!placa || placa === '—') return
      this.$emit('search-murphy-placa', String(placa).trim())
    },

    // Cross-ref VIN→placa-asociada: lanza una nueva consulta Murphy con la
    // placa histórica del mismo VIN, para auditar el reporte ligado a esa
    // placa antigua.
    onSearchPlacaAsociada(placa) {
      if (!placa) return
      this.$emit('search-murphy-placa', String(placa).trim())
    },

    // Calcula la categoría visual del reporte (robo / recuperado / ok)
    // a partir del result. Se expone como método (no computed) para poder
    // consultarlo durante el watcher de `murphyResult.id` antes de que el
    // computed `reporteCategory` haya recibido el nuevo valor.
    reporteCategoryFor(result) {
      if (!result || !result.data) return 'ok'
      return categorizeReporte(result.data.reporte)
    },

    // Texto del campo `motivo` ALPR para mostrar en el header del item.
    // Se omite cuando el backend devuelve placeholder ("SIN INFORMACION",
    // string vacío, etc.) — esos casos no agregan información al operador.
    lprMotivo(item) {
      var raw = this.getLprProp(item, 'motivo')
      if (!raw || raw === '—') return ''
      var s = String(raw).trim()
      if (!s || /^sin\s+informaci[oó]n$/i.test(s)) return ''
      return s
    },

    // Formatea ESTATUS.fecha_robo (ISO `2026-01-21T02:00:00.000Z`) a
    // `dd/mm/yyyy`. Si ya viene en formato local (caso ALERTA), lo deja
    // tal cual. Defensivo ante valores no parseables.
    formatRoboDate(raw) {
      if (!raw) return ''
      var s = String(raw)
      // Ya en formato dd/m/yyyy o dd/mm/yyyy → lo dejamos.
      if (/^\d{1,2}\/\d{1,2}\/\d{2,4}/.test(s)) return s
      var d = new Date(s)
      if (Number.isNaN(d.getTime())) return s
      var pad = function(n) { return String(n).padStart(2, '0') }
      return pad(d.getDate()) + '/' + pad(d.getMonth() + 1) + '/' + d.getFullYear()
    },

    // Copiar CURP al portapapeles (utility de persona).
    onCopyCurp() {
      var p = this.currentPersona
      var curp = p && p.ine && p.ine.curp
      if (!curp) return
      try {
        navigator.clipboard.writeText(curp)
        if (window.VMA && window.VMA.showMessage) {
          window.VMA.showMessage({ title: 'CURP copiado', color: 'success' })
        }
      } catch (e) {
        // Fallback textarea si no hay clipboard (http o privado).
        var ta = document.createElement('textarea')
        ta.value = curp
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        try { document.execCommand('copy') } catch (e2) { /* ignore */ }
        document.body.removeChild(ta)
      }
    },


    onCopy() {
      if (!this.hasResult || !this.isFresh) return
      let text = ''
      if (this.kind === 'vehiculo') {
        const d = this.vehiculoData
        const lines = [
          'Placa: ' + (d.placa || '—'),
          'Estado: ' + (d.estado || '—'),
          'Reporte: ' + (d.reporte || 'SIN REPORTE'),
          'Propietario: ' + (d.propietario || '—'),
          'Vehículo: ' + (d.vehiculo || '—'),
          'Color: ' + (d.color || '—'),
          'Serie: ' + (d.serie || '—'),
        ]
        if (d.tipo) lines.push('Tipo: ' + d.tipo)
        if (d.motor) lines.push('Motor: ' + d.motor)
        if (d.rfc) lines.push('RFC: ' + d.rfc)
        if (d.domicilioPropietario) lines.push('Domicilio del propietario: ' + d.domicilioPropietario)
        if (d.observaciones) lines.push('Observaciones: ' + d.observaciones)
        // Sección de robo, solo cuando hay info.
        const r = this.roboData
        if (r) {
          lines.push('')
          lines.push('━━ Reporte de robo ━━')
          if (r.estatusOficial) lines.push('Estatus oficial: ' + r.estatusOficial)
          if (r.fechaRobo) lines.push('Fecha del robo: ' + this.formatRoboDate(r.fechaRobo))
          if (r.fechaReporte && r.fechaReporte !== r.fechaRobo) lines.push('Fecha del reporte: ' + r.fechaReporte)
          if (r.modalidad) lines.push('Modalidad: ' + r.modalidad)
          if (this.lugarRobo) lines.push('Lugar del robo: ' + this.lugarRobo)
          if (r.direccionRobo) lines.push('Dirección del robo: ' + r.direccionRobo)
          if (r.averiguacion) lines.push('Averiguación: ' + r.averiguacion)
          if (r.averiguacionPrevia && r.averiguacionPrevia !== r.averiguacion) {
            lines.push('Averiguación previa: ' + r.averiguacionPrevia)
          }
          if (r.folioDenuncia) lines.push('Folio de denuncia: ' + r.folioDenuncia)
        }
        // Historial de placas con el mismo VIN.
        if (this.placasAsociadas.length) {
          lines.push('')
          lines.push('Otras placas con el mismo VIN: ' + this.placasAsociadas.join(', '))
        }
        text = lines.join('\n')
        text += this.$_formatLprDataForCopy(this.lprData)
      } else if (this.kind === 'persona' && this.currentPersona) {
        text = this.$_scrapeMurphyPersonaText()
      }
      if (text) {
        navigator.clipboard.writeText(text).catch(() => {
          const ta = document.createElement('textarea')
          ta.value = text
          ta.style.position = 'fixed'
          ta.style.opacity = '0'
          document.body.appendChild(ta)
          ta.select()
          try { document.execCommand('copy') } catch (e) { /* ignore */ }
          document.body.removeChild(ta)
        })
        if (window.VMA && window.VMA.showMessage) {
          window.VMA.showMessage({ title: 'Copiado al portapapeles', color: 'success' })
        }
      }
    },

    $_scrapeMurphyPersonaText() {
      const resultEl = this.$el ? this.$el.querySelector('.murphy-result') : null
      if (!resultEl) {
        return [
          'Nombre: ' + this.personaNombre,
          'Nacimiento: ' + ((this.currentPersona && this.currentPersona.fecha_nac) || '—'),
          'Estado: ' + ((this.currentPersona && this.currentPersona.estado) || '—'),
        ].join('\n')
      }
      const lines = []
      const pushDetailRow = (rowEl, indent) => {
        const labelEl = rowEl.querySelector('.detail-row__label')
        const valueEl = rowEl.querySelector('.detail-row__value')
        const label = labelEl ? labelEl.textContent.trim() : ''
        const value = valueEl ? valueEl.textContent.trim() : ''
        if (!label && !value) return
        lines.push(indent + (label ? label + ': ' : '') + value)
      }
      const children = resultEl.children
      for (let i = 0; i < children.length; i++) {
        const c = children[i]
        if (c.classList.contains('murphy-section-title')) {
          if (lines.length) lines.push('')
          lines.push('━━ ' + c.textContent.trim() + ' ━━')
        } else if (c.classList.contains('detail-row')) {
          pushDetailRow(c, '')
        } else if (c.classList.contains('murphy-sub-item')) {
          const subRows = c.querySelectorAll('.detail-row')
          for (let j = 0; j < subRows.length; j++) pushDetailRow(subRows[j], '  ')
          if (subRows.length && i + 1 < children.length
            && children[i + 1].classList.contains('murphy-sub-item')) {
            lines.push('')
          }
        } else if (c.classList.contains('murphy-foto')) {
          const titleEl = c.querySelector('.murphy-section-title')
          if (titleEl) {
            if (lines.length) lines.push('')
            lines.push('━━ ' + titleEl.textContent.trim() + ' ━━')
            lines.push('[Fotografía disponible en pantalla]')
          }
        }
      }
      return lines.join('\n').replace(/\n{3,}/g, '\n\n')
    },

    $_formatLprDataForCopy(lprArray) {
      if (!Array.isArray(lprArray) || lprArray.length === 0) return ''
      const lines = ['Detecciones LPR (' + lprArray.length + '):']
      for (let i = 0; i < lprArray.length; i++) {
        const item = lprArray[i]
        if (Array.isArray(item)) {
          lines.push('')
          lines.push((i + 1) + '. ' + this.getLprProp(item, 'registro'))
          for (let j = 0; j < item.length; j++) {
            const f = item[j]
            if (f && f.name && f.name !== 'registro' && !LPR_IMAGE_FIELDS[f.name]) {
              const val = f.value
              if (val !== null && val !== undefined && val !== '') {
                const label = LPR_FIELD_LABELS[f.name] || f.name
                lines.push('   ' + label + ': ' + val)
              }
            }
          }
        }
      }
      return '\n\n' + lines.join('\n')
    },

    onRefresh() {
      this.refetchMurphyResult()
    },

    onTogglePin() {
      if (!this.murphyResult) return
      if (this.isPinnedResult) {
        this.unpinMurphy(this.murphyResult.id)
      } else {
        this.pinCurrentMurphy()
      }
    },

    toggleLprExpanded(idx) {
      this.murphyLprExpandedIdx = this.murphyLprExpandedIdx === idx ? -1 : idx
    },

    formatLprDateShort(raw) {
      if (!raw || raw === '—') return '—'
      const m = String(raw).match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})\s+(\d{1,2}):(\d{2})/)
      if (m) {
        const dd = m[1].padStart(2, '0')
        const mm = m[2].padStart(2, '0')
        const yyyy = m[3].length === 2 ? '20' + m[3] : m[3]
        const hh = m[4].padStart(2, '0')
        return dd + '/' + mm + '/' + yyyy + ' ' + hh + ':' + m[5]
      }
      const d = new Date(raw)
      if (!Number.isNaN(d.getTime())) {
        const pad = (n) => String(n).padStart(2, '0')
        return pad(d.getDate()) + '/' + pad(d.getMonth() + 1) + '/' + d.getFullYear()
          + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes())
      }
      return raw
    },

    getLprProp(item, prop) {
      if (!Array.isArray(item)) return '—'
      for (let i = 0; i < item.length; i++) {
        if (item[i] && item[i].name === prop) return item[i].value || '—'
      }
      return '—'
    },

    // Parsea el campo "registro" de Murphy (formato "DD/MM/YYYY HH:mm:ss")
    // a timestamp ms. Date.parse() default interpreta MM/DD, lo que hace
    // NaN cualquier día > 12 (ej. "18/04/2026" → mes 18 → NaN). Causaba
    // que esos hits se descartaran completamente del mapa y de la tabla.
    parseLprRegistro(raw) {
      if (raw == null) return NaN
      if (typeof raw === 'number') return raw
      const s = String(raw).trim()
      if (!s) return NaN
      const parts = s.split(' ')
      const dmy = parts[0].split('/')
      if (dmy.length !== 3) return Date.parse(s)
      const time = parts[1] || '00:00:00'
      const iso = dmy[2] + '-' + String(dmy[1]).padStart(2, '0') + '-' + String(dmy[0]).padStart(2, '0') + 'T' + time
      return Date.parse(iso)
    },

    // ¿Podemos geolocalizar este arco en el mapa? Solo si el nombre del
    // arco (normalizado a upper/trim) existe como key en lprArcoIndex.
    canLocateLprArco(arco) {
      if (!arco || arco === '—') return false
      const key = String(arco).trim().toUpperCase()
      return !!this.lprArcoIndex[key]
    },

    onFlyToLprArco(arco) {
      if (!this.canLocateLprArco(arco)) return
      this.$emit('fly-to-lpr-arco', arco)
    },

    // Click en una fila de la tabla de detecciones LPR: resalta el
    // círculo correspondiente en el mapa (escala 1.5x, paridad legacy
    // centerLpr) y hace flyTo. El expand/collapse del row sigue.
    onLprRowSelect(idx) {
      this.toggleLprExpanded(idx)
      this.$emit('select-lpr-hit', idx)
    },

    getLprExpandedFields(item) {
      if (!Array.isArray(item)) return []
      const HIDDEN = { arco: true, registro: true } // ya visibles en el header
      const out = []
      for (let i = 0; i < item.length; i++) {
        const f = item[i]
        if (f && f.name && !HIDDEN[f.name]) {
          const value = f.value
          if (value !== null && value !== undefined && value !== '') {
            const isImage = !!LPR_IMAGE_FIELDS[f.name]
            out.push({
              label: LPR_FIELD_LABELS[f.name] || f.name,
              value: isImage ? this.buildLprImageSrc(value) : value,
              isImage,
            })
          }
        }
      }
      return out
    },

    buildLprImageSrc(value) {
      if (!value) return ''
      const v = String(value).trim()
      if (v.indexOf('data:image') === 0) return v
      if (v.indexOf('http') === 0) return v
      return 'data:image/jpeg;base64,' + v
    },

    openLprZoom(src) {
      if (!src) return
      this.murphyLprZoomSrc = src
      this.murphyLprZoomScale = 1
      window.addEventListener('keydown', this._onLprZoomKeydown)
    },

    // Abrir lightbox con la foto de la persona (base64). Reusa la misma
    // infraestructura del zoom LPR.
    openPersonaPhoto(b64) {
      if (!b64) return
      this.openLprZoom('data:image/jpeg;base64,' + b64)
    },

    closeLprZoom() {
      if (!this.murphyLprZoomSrc) return
      this.murphyLprZoomSrc = ''
      this.murphyLprZoomScale = 1
      window.removeEventListener('keydown', this._onLprZoomKeydown)
    },

    toggleLprZoomScale() {
      this.murphyLprZoomScale = this.murphyLprZoomScale >= 2.5 ? 1 : this.murphyLprZoomScale + 0.5
    },

    zoomInLpr() { this.murphyLprZoomScale = Math.min(4, this.murphyLprZoomScale + 0.5) },
    zoomOutLpr() { this.murphyLprZoomScale = Math.max(1, this.murphyLprZoomScale - 0.5) },
    zoomResetLpr() { this.murphyLprZoomScale = 1 },

    _onLprZoomKeydown(e) {
      if (e.key === 'Escape') { this.closeLprZoom(); return }
      const t = e.target
      if (t && t.matches && t.matches('input, textarea, [contenteditable="true"]')) return
      if (e.key === '+' || e.key === '=') this.zoomInLpr()
      else if (e.key === '-') this.zoomOutLpr()
      else if (e.key === '0') this.zoomResetLpr()
    },
  },
}
</script>

<style scoped>
.murphy-drawer {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--kln-bg-surface);
  border-right: 1px solid var(--kln-border-dim);
  overflow: hidden;
}

.murphy-drawer__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid var(--kln-border-dim);
  background: rgba(0, 0, 0, 0.25);
  flex-shrink: 0;
}

.murphy-drawer__logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  line-height: 0;
}

.murphy-drawer__title-wrap {
  flex: 1;
  min-width: 0;
}

.murphy-drawer__title {
  font-size: 13px;
  font-weight: 600;
  color: var(--kln-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.murphy-drawer__subtitle {
  font-size: 10px;
  color: var(--kln-text-dim);
  margin-top: 1px;
}

.murphy-drawer__actions {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
}

.murphy-drawer__action {
  width: 26px !important;
  height: 26px !important;
}

.murphy-drawer__body {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.murphy-drawer__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 16px;
  min-height: 160px;
  text-align: center;
}

.murphy-drawer__empty--error {
  color: var(--kln-alert-danger);
}

.murphy-drawer__empty-text {
  font-size: 12px;
  color: var(--kln-text-dim);
  line-height: 1.5;
}

.murphy-drawer__empty-text code {
  background: var(--kln-border-dim);
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
  color: var(--kln-text);
}

.murphy-drawer__placeholder {
  padding: 16px;
  text-align: center;
  color: var(--kln-text-dim);
  font-size: 12px;
  font-style: italic;
}

/* ── Murphy vehiculo result ───────────────────────────────────── */
.murphy-result {
  padding: 0;
}

/* Banner del reporte: contenedor en columna con dos hijos:
   - .murphy-report__row : línea 1 (icono + headline + timestamp)
   - .murphy-report__sub : línea 2 (descripción del subestado, opcional)
   El layout en columna evita el solapamiento que ocurría con
   icon+label+chip+timestamp en una sola línea horizontal cuando el
   ancho del drawer es estrecho. */
.murphy-report {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  border-radius: 6px;
  /* margin-bottom reducido: el banner ya tiene padding interno y el
     primer row de zona 1 (Propietario) tiene su propio padding vertical. */
  margin-bottom: 4px;
}

.murphy-report__row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1.2;
}

.murphy-report__icon {
  flex-shrink: 0;
}

/* Banner de reporte tri-estado:
   - robo       → rojo  (CON REPORTE / PREDENUNCIA / cualquier reporte activo)
   - recuperado → azul accent (hubo robo, ya recuperado — sigue siendo info
                  operativa relevante para investigación)
   - ok         → verde (SIN REPORTE)
   El sub-badge `.murphy-report__estatus` distingue CONFIRMADO vs PREDENUNCIA
   cuando aplica (info de ALERTA VEHICULAR.estatus). */
.murphy-report--robo {
  background: rgba(255, 76, 81, 0.15);
  color: var(--kln-alert-danger);
}

.murphy-report--recuperado {
  background: var(--kln-accent-soft);
  color: var(--kln-accent);
}

.murphy-report--ok {
  background: rgba(30, 149, 83, 0.15);
  color: var(--kln-alert-success);
}

/* Compat con código legacy que usa .murphy-report--alert (otros lugares
   pueden referenciarlo). Mismo estilo que --robo. */
.murphy-report--alert {
  background: rgba(255, 76, 81, 0.15);
  color: var(--kln-alert-danger);
}

.murphy-report__label {
  /* Headline del estado del reporte. Sin truncamiento: la línea 1 ahora
     tiene espacio completo (no compite con un chip por el ancho), así
     que las etiquetas posibles ("CON REPORTE", "PRE-DENUNCIA",
     "RECUPERADO", "SIN REPORTE") caben sin ellipsis. */
  flex: 1 1 auto;
  min-width: 0;
}

/* Timestamp pegado al borde derecho de la línea 1. */
.murphy-report__timestamp {
  font-size: 11px;
  font-weight: 500;
  opacity: 0.7;
  white-space: nowrap;
  flex-shrink: 0;
}

/* Línea 2: descripción legible del subestado (ej. "Confirmado por
   fiscalía", "Reporte preliminar — sin confirmación de fiscalía",
   "Vehículo recuperado"). Tipografía más pequeña y peso medio para
   que no compita visualmente con el headline. Sangría que la alinea
   con el inicio del texto del headline (después del icono). */
.murphy-report__sub {
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.01em;
  opacity: 0.85;
  padding-left: 22px;
  line-height: 1.35;
}

/* Lista de placas asociadas al mismo VIN (historial). */
.murphy-placa-asociada {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 4px;
  font-size: 12px;
  color: var(--kln-text);
  font-variant-numeric: tabular-nums;
  border-bottom: 1px solid var(--kln-border-dim);
}

.murphy-placa-asociada:last-child {
  border-bottom: none;
}

.murphy-placa-asociada__value {
  font-weight: 600;
  letter-spacing: 0.04em;
}

.murphy-section-title {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--kln-text-muted);
  padding: 0 0 0 4px;
  border-top: 1px solid var(--kln-border-dim);
  margin-top: 0;
}

/* ── LPR accordion ───────────────────────────────────────────── */
.murphy-lpr-section {
  margin-top: 8px;
}

/* Badge numérico en cada fila de la tabla. El número coincide con el
   del punto correspondiente en el mapa para que el operador pueda
   correlacionar visualmente fila ↔ punto. Color del fondo = accent
   del tema (mismo color que los puntos del mapa). */
.murphy-lpr-hit-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 9px;
  background: var(--kln-accent);
  color: var(--kln-bg-deep);
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
}

.murphy-lpr-accordion {
  max-height: 260px;
  overflow-y: auto;
  margin-top: 6px;
  border: 1px solid var(--kln-border-dim);
  border-radius: 6px;
  background: var(--kln-border-dim);
}

.murphy-lpr-item + .murphy-lpr-item {
  border-top: 1px solid var(--kln-border-dim);
}

.murphy-lpr-header {
  /* El header ahora puede tener 2 líneas: línea 1 con fecha+arco+locate,
     línea 2 (opcional) con el motivo del hit. Cambiamos de `flex row` a
     `flex column` para apilar las dos líneas; la línea 1 conserva su
     layout horizontal vía `.murphy-lpr-header__line1`. */
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 2px;
  width: 100%;
  padding: 7px 10px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: 11px;
  color: var(--kln-text-muted);
  transition: background-color 0.12s ease;
}

.murphy-lpr-header__line1 {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Motivo del hit — texto libre con info clave del operativo (ej. "PROBABLE
   PRIV DE LIBERTAD"). Se pinta amarillo ámbar para que el operador lo
   detecte al instante sin tener que expandir cada fila. Truncado a 2
   líneas con ellipsis para que el header no crezca sin control. */
.murphy-lpr-header__motivo {
  margin-left: 26px;  /* alineado con el inicio del texto de la línea 1 */
  font-size: 10.5px;
  font-weight: 500;
  line-height: 1.35;
  color: var(--kln-status-warning, #f5a524);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.murphy-lpr-header:hover {
  background: var(--kln-border-dim);
}

.murphy-lpr-item--open .murphy-lpr-header {
  background: var(--kln-accent-soft);
}

.murphy-lpr-chevron {
  color: var(--kln-text-dim) !important;
  transition: transform 0.18s ease;
  flex-shrink: 0;
}

.murphy-lpr-item--open .murphy-lpr-chevron {
  transform: rotate(90deg);
  color: var(--kln-accent) !important;
}

.murphy-lpr-header-date {
  color: var(--kln-text);
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}

.murphy-lpr-header-arch {
  color: var(--kln-text-muted);
  font-size: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.murphy-lpr-locate {
  width: 20px !important;
  height: 20px !important;
  flex-shrink: 0;
  opacity: 0.7;
  transition: opacity 0.15s, background 0.15s;
}

.murphy-lpr-locate:hover {
  opacity: 1;
  background: var(--kln-accent-soft) !important;
}

.murphy-lpr-body {
  padding: 6px 12px 10px 32px;
  background: rgba(0, 0, 0, 0.18);
}

.murphy-lpr-field {
  display: flex;
  gap: 8px;
  padding: 3px 0;
  font-size: 11px;
  line-height: 1.35;
}

.murphy-lpr-field + .murphy-lpr-field {
  border-top: 1px solid var(--kln-border-dim);
}

.murphy-lpr-field-label {
  flex: 0 0 78px;
  color: var(--kln-text-dim);
  text-transform: uppercase;
  font-size: 9px;
  letter-spacing: 0.04em;
  padding-top: 2px;
}

.murphy-lpr-field-value {
  flex: 1;
  color: var(--kln-text-muted);
  word-break: break-word;
}

.murphy-lpr-field--image {
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.murphy-lpr-field--image .murphy-lpr-field-label {
  flex: none;
}

.murphy-lpr-field-image {
  display: block;
  width: 100%;
  max-width: 220px;
  max-height: 180px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid var(--kln-border-dim);
  background: rgba(0, 0, 0, 0.4);
  cursor: zoom-in;
  transition: border-color 0.15s ease;
}

.murphy-lpr-field-image:hover {
  border-color: var(--kln-border-accent);
}

/* ── Persona: homonym paginator + filters ─────────────────────── */
.murphy-homonym-controls {
  border-bottom: 1px solid var(--kln-border-dim);
  flex-shrink: 0;
}

/* Placeholder cuando los filtros dejan 0 resultados visibles. */
.murphy-no-results {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 12px 14px;
  margin: 10px;
  background: var(--kln-border-dim);
  border: 1px dashed var(--kln-border-normal);
  border-radius: 8px;
  font-size: 12px;
  color: var(--kln-text-muted);
  line-height: 1.4;
}

.murphy-paginator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 10px;
}

.murphy-paginator__text {
  font-size: 11px;
  color: var(--kln-text-muted);
  min-width: 80px;
  text-align: center;
}

.murphy-filter-toggle--active {
  background: var(--kln-accent-soft) !important;
}

.murphy-filters {
  display: flex;
  gap: 4px;
  padding: 2px 10px 6px;
}

/* Estado ocupa el ancho sobrante; año queda fijo en ~72 px (4 dígitos +
   ícono + padding). Desperdiciaba espacio cuando ambos eran flex:1. */
.murphy-filters > .murphy-filter-input--estados {
  flex: 1 1 0;
  min-width: 0;
}

.murphy-filters > .murphy-filter-input--year {
  flex: 0 0 82px;
  min-width: 0;
}

.murphy-filters >>> .v-input__slot {
  min-height: 28px !important;
  padding: 0 8px !important;
}

.murphy-filters >>> input,
.murphy-filters >>> .v-select__selection {
  font-size: 11px !important;
  padding: 2px 0 !important;
}

.murphy-filters >>> .v-select__slot {
  max-height: 28px;
}

.murphy-filters >>> .v-input__append-inner {
  margin-top: 4px !important;
}

/* Chip del estado seleccionado: inline-block para que text-overflow funcione.
   max-width ajustado para dejar espacio al chip "+N" y a los íconos del
   v-select (clear + chevron), sin desbordar el input. Si el nombre no
   cabe, se trunca con ellipsis y el título nativo muestra el nombre
   completo en hover. */
.murphy-estado-chip {
  display: inline-block;
  padding: 2px 8px;
  margin: 1px 3px 1px 0;
  background: var(--kln-accent-soft, rgba(0, 212, 255, 0.12));
  color: var(--kln-accent, #00D4FF);
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1.5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
  max-width: calc(100% - 40px);
}

.murphy-estado-chip--more {
  background: var(--kln-border-dim);
  color: var(--kln-text-muted);
  font-weight: 700;
}

/* ── Dropdown de estados con conteo alineado en columna fija ──
   El menú se teleporta fuera del scoped, por eso estos estilos viven
   en el <style> no-scoped del archivo (ver al final). */

.murphy-filters >>> .v-input__icon {
  width: 16px !important;
  min-width: 16px !important;
  height: 16px !important;
}

.murphy-filters >>> .v-input__icon .v-icon {
  font-size: 14px !important;
}

.murphy-filters >>> .v-input__icon--clear {
  opacity: 1 !important;
}

/* ── Persona: sub-item blocks + section alert ─────────────────── */
.murphy-section-title--alert {
  color: var(--kln-alert-danger);
}

.murphy-sub-item {
  padding: 4px 0;
  margin-bottom: 4px;
}

/* ── Persona: foto ────────────────────────────────────────────── */
.murphy-foto {
  text-align: center;
  padding: 8px 0;
}

.murphy-foto-img {
  max-width: 60%;
  border-radius: 8px;
  border: 1px solid var(--kln-border-normal);
}
</style>

<style>
/* LPR lightbox — not scoped (fixed overlay outside component DOM tree) */
.lpr-zoom-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(5, 8, 16, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  animation: lprZoomFadeIn 0.14s ease;
}

@keyframes lprZoomFadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.lpr-zoom-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(20, 24, 36, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease;
}

.lpr-zoom-close:hover {
  background: rgba(255, 76, 81, 0.2);
  border-color: rgba(255, 76, 81, 0.4);
}

.lpr-zoom-toolbar {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: rgba(20, 24, 36, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px;
}

.lpr-zoom-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease;
}

.lpr-zoom-btn:hover:not(:disabled) {
  background: var(--kln-accent-soft);
}

.lpr-zoom-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.lpr-zoom-level {
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.75);
  min-width: 42px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.lpr-zoom-stage {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  padding: 72px 24px 24px;
}

.lpr-zoom-image {
  max-width: 92vw;
  max-height: 86vh;
  object-fit: contain;
  border-radius: 6px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.6);
  cursor: zoom-in;
  transition: transform 0.18s ease;
  transform-origin: center center;
  user-select: none;
  -webkit-user-drag: none;
}

/* Detail row — not scoped so functional component can use */
.detail-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  border-bottom: 1px solid var(--kln-border-dim);
}

.detail-row__icon {
  color: var(--kln-text-dim) !important;
  flex-shrink: 0;
}

.detail-row__label {
  font-size: 11px;
  color: var(--kln-text-dim);
  flex-shrink: 0;
}

.detail-row__value {
  font-size: 12px;
  color: var(--kln-text-muted);
  flex: 1;
  text-align: left;
  word-break: break-word;
}

.detail-row__value a {
  color: var(--kln-accent);
  text-decoration: none;
}

.detail-row__value a:hover {
  text-decoration: underline;
}

/* ── Opciones del dropdown de estados Murphy ──
   Teleport del v-select → vive fuera del scoped. Columnas fijas:
     [check] [N] (36 px, tabular) [name] — nombres alineados sin
   importar los dígitos del conteo. */
.murphy-estados-menu .v-list-item__content.murphy-estado-opt__content {
  display: flex !important;
  flex-direction: row !important;
  align-items: center;
  gap: 8px;
  padding: 0 !important;
}

.murphy-estados-menu .murphy-estado-opt__check {
  margin: 0 8px 0 0 !important;
  min-width: 0 !important;
}

.murphy-estados-menu .murphy-estado-opt__count {
  flex: 0 0 32px;
  width: 32px;
  font-variant-numeric: tabular-nums;
  font-family: 'Geist Mono', 'JetBrains Mono', 'Menlo', monospace;
  font-size: 10px !important;
  font-weight: 600;
  color: var(--kln-accent) !important;
  text-align: left;
  letter-spacing: 0;
}

/* Count=0 atenuado: el ojo va primero a estados con matches reales. */
.murphy-estados-menu .murphy-estado-opt__count--zero {
  color: var(--kln-text-dim) !important;
}

.murphy-estados-menu .murphy-estado-opt__name {
  flex: 1 1 auto;
  font-size: 12px !important;
  font-weight: 500;
  color: var(--kln-text) !important;
  letter-spacing: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ─── Sistema de zonas (DRAWERS-DESIGN-SPEC) ──────────────────────────
   Espaciados y separadores uniformes con MapDrawer (derecho). */

.murphy-drawer .drawer-zone {
  padding: 0 12px;
}

.murphy-drawer .drawer-zone + .drawer-zone {
  border-top: 1px solid var(--kln-border-subtle, rgba(255, 255, 255, 0.08));
  /* Padding-top reducido: maximiza el espacio para el listado de
     Detecciones LPR. El border-top + margin-top dan separacion visual
     suficiente entre zonas. */
  margin-top: 4px;
  padding-top: 6px;
}

.murphy-drawer .drawer-zone--1 {
  /* Padding compacto en zona 1: queremos ganar espacio vertical para
     el accordion de Detecciones LPR de abajo, manteniendo la
     legibilidad de los rows clave (Propietario, Placa, etc). */
  padding-top: 6px;
  padding-bottom: 6px;
}

.murphy-drawer .drawer-zone--2 {
  /* Zona 2 = la linea "Sin reportes activos. Consultado hace ...".
     Es mensaje contextual de una sola linea, no necesita padding
     vertical generoso. */
  padding-top: 8px;
  padding-bottom: 8px;
}

.murphy-drawer .drawer-zone__context {
  font-size: 12px;
  color: var(--kln-text-muted, rgba(255, 255, 255, 0.6));
  /* margin-bottom era 8px y dejaba aire muerto bajo el texto cuando la
     zona solo tiene este hijo (caso normal del vehiculo). */
  line-height: 1.4;
}

.murphy-drawer .drawer-zone__context--alert {
  color: var(--kln-alert-danger, #ff5252);
  background: var(--kln-accent-soft, rgba(255, 82, 82, 0.08));
  border-left: 2px solid var(--kln-alert-danger, #ff5252);
  padding: 8px 10px;
  border-radius: 3px;
  font-weight: 500;
}

/* Mensaje "OK" — persona consultada sin antecedentes. Verde sutil,
   comunica "esta persona está limpia" para que el operador no tenga
   que scrollear buscando algo que no va a encontrar. */
.murphy-drawer .drawer-zone__context--ok {
  color: var(--kln-status-online, #1E9553);
  background: rgba(30, 149, 83, 0.08);
  border-left: 2px solid var(--kln-status-online, #1E9553);
  padding: 8px 10px;
  border-radius: 3px;
  font-weight: 500;
}

.murphy-drawer .drawer-zone--3 {
  padding-top: 12px;
  padding-bottom: 16px;
}

.murphy-drawer .drawer-zone--4 {
  /* Datos administrativos: colapsado por default. Antes habia 24 px de
     padding-bottom que creaba un bloque vacio enorme bajo el header
     cerrado. Reducido a 8 px — el border-top de la siguiente zona ya da
     separacion visual suficiente. */
  padding-top: 8px;
  padding-bottom: 8px;
}

.murphy-drawer .drawer-zone__content {
  padding-top: 4px;
}

/* DrawerSectionHeader (componente hijo) trae border-top propio para
   separar secciones dentro de una misma zona. Cuando es el PRIMER hijo
   de una zona, la zona ya tiene su propio border-top → resultado: doble
   linea visible. Eliminamos el border del header en ese caso para que
   solo quede una linea. La specificidad del padre gana al scoped del
   child (3 clases vs 1 clase + atributo data-v). */
.murphy-drawer .drawer-zone > .drawer-section-header:first-child {
  border-top: none;
}

/* Foto "hero" de la persona en Zona 1: visible a primera vista.
   Click para ampliar vía lightbox compartido del LPR. */
.persona-photo-hero {
  display: flex;
  justify-content: center;
  padding: 4px 0 12px 0;
}

.persona-photo-hero__img {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid var(--kln-border-accent, rgba(255, 255, 255, 0.15));
  cursor: zoom-in;
  transition: border-color 0.15s, transform 0.15s;
}

.persona-photo-hero__img:hover {
  border-color: var(--kln-accent, #00D4FF);
  transform: scale(1.02);
}
</style>
